import { useMemo, useState } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronLeft, IconChevronRight, IconShare, IconX } from '@tabler/icons-react';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import { MAP_CAROUSEL_BREAKPOINT_TIERS } from 'components/marketplace/helpers/presizedPhotos';
import { MAP_MINI_PDP_CAROUSEL_LAYOUT_CLASS } from 'components/marketplace/map/types';
import { Carousel } from 'components/marketplace/shared/Carousel';
import { CarouselDots } from 'components/marketplace/shared/CarouselDots';
import { GalleryListingPhoto } from 'components/marketplace/shared/GalleryListingPhoto';

interface PhotoCarouselProps {
  photos: GalleryPhoto[] | null | undefined;
  onClose: () => void;
  onOpenShareModal: () => void;
  isRounded?: boolean;
  onPhotoClick?: () => void;
}

export const PhotoCarousel = ({
  photos,
  onClose,
  isRounded = false,
  onOpenShareModal,
  onPhotoClick,
}: PhotoCarouselProps) => {
  const [index, setIndex] = useState(0);

  const carouselPhotos = useMemo(() => photos ?? [], [photos]);
  const count = carouselPhotos.length;

  return (
    <Carousel
      count={count}
      index={index}
      onIndexChange={setIndex}
      ariaLabel="Property photos"
      // shrink-0: the carousel sits at the top of a fixed-height, scrollable
      // flex column (the mini-PDP panel). Without it, the tall content below
      // shrinks the aspect-ratio carousel to zero height (CSS flex items default
      // to shrink:1, unlike the React Native default of 0 in the old bricks UI).
      //
      // Mobile map mini PDP (< md): square aspect + fixed heights to prevent CLS.
      // Desktop mini PDP (>= md): 16:9 aspect, height from aspect ratio.
      className={cn(
        MAP_MINI_PDP_CAROUSEL_LAYOUT_CLASS,
        'bg-od-bg-secondary',
        isRounded && 'rounded-t-4xl',
      )}
      renderSlide={(i) => {
        const photo = carouselPhotos[i];
        if (!photo) {
          return null;
        }
        return (
          <GalleryListingPhoto
            photo={photo}
            sizes={MAP_CAROUSEL_BREAKPOINT_TIERS}
            lazy={i !== 0}
            preload={i === 0}
            fetchPriority={i === 0 ? 'high' : undefined}
            alt={
              photo.isFloorPlan ? photo.label?.trim() || 'Floor plan' : `Property photo ${i + 1}`
            }
            objectFit="cover"
            fit="cover"
          />
        );
      }}
    >
      <Button
        variant="secondary"
        aria-label="Close property details"
        analyticsName="marketplace-carousel-back-button"
        onClick={onClose}
        className="absolute left-6 top-6 z-[1] size-9 rounded-full border-od-border-tertiary p-0"
      >
        <IconX size={24} />
      </Button>
      <Button
        variant="secondary"
        aria-label="share"
        analyticsName="marketplace-carousel-share-button"
        onClick={onOpenShareModal}
        className="absolute right-6 top-6 z-[1] size-9 rounded-full border-od-border-tertiary p-0"
      >
        <IconShare size={24} />
      </Button>
      {index > 0 && (
        <Button
          variant="secondary"
          aria-label="Previous photo"
          analyticsName="marketplace-carousel-prev-button"
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          // Arrows only from the `sm` breakpoint (640px) up — the nearest named
          // breakpoint to the old carousel's 600px cutoff; on narrow phones
          // they'd overlap the photo.
          className="absolute left-6 top-1/2 z-[1] hidden size-7 -translate-y-1/2 rounded-full border-od-border-tertiary p-0 sm:flex"
        >
          <IconChevronLeft size={20} />
        </Button>
      )}
      {index < count - 1 && (
        <Button
          variant="secondary"
          aria-label="Next photo"
          analyticsName="marketplace-carousel-next-button"
          onClick={() => setIndex((i) => Math.min(count - 1, i + 1))}
          className="absolute right-6 top-1/2 z-[1] hidden size-7 -translate-y-1/2 rounded-full border-od-border-tertiary p-0 sm:flex"
        >
          <IconChevronRight size={20} />
        </Button>
      )}
      <Box
        className="pointer-events-none absolute bottom-5 left-0 right-0 z-[1] items-center justify-center"
        aria-hidden="true"
      >
        <CarouselDots length={count} currentIndex={index} />
      </Box>
      {onPhotoClick && (
        // Transparent full-bleed tap target. No z-index, so the z-[1] controls
        // above stay clickable; the Carousel suppresses the click that follows
        // a swipe so this only fires on a genuine tap.
        <Box
          data-carousel-photo-tap
          aria-label="View property details"
          onClick={onPhotoClick}
          analyticsName="marketplace-carousel-photo-clicked"
          className="absolute inset-0 h-full w-full bg-transparent p-0"
        />
      )}
    </Carousel>
  );
};
